import {
  getLeoUuid,
  LeoUUID,
  getLeoTimestamp,
  LeoTimestamp,
  getObject,
  getBoolean,
} from "@surya-digital/leo-ts-types";
import { BoundedName, BoundedNameDTO } from "../types/boundedName";
import { KeycloakEmail, KeycloakEmailDTO } from "../types/keycloakEmail";
import { EmailContent, EmailContentDTO } from "./emailContent";
import { ScheduleType } from "./scheduleType";
import { EmailCycleStatus } from "./emailCycleStatus";

export interface EmailCycleDetailsDTO {
  id: string;
  scheduledAt: string;
  cycleName: BoundedNameDTO;
  displayCycleName: BoundedNameDTO;
  senderEmail: KeycloakEmailDTO;
  senderName: BoundedNameDTO;
  emailContent: EmailContentDTO;
  scheduleType: ScheduleType.ScheduleTypeDTO;
  status: EmailCycleStatus.EmailCycleStatusDTO;
  isScheduleEditable: boolean;
}

export class EmailCycleDetails {
  readonly isEmailCycleDetailsEmailCycleDetails: boolean = true;

  readonly id: LeoUUID;
  readonly scheduledAt: LeoTimestamp;
  readonly cycleName: BoundedName;
  readonly displayCycleName: BoundedName;
  readonly senderEmail: KeycloakEmail;
  readonly senderName: BoundedName;
  readonly emailContent: EmailContent;
  readonly scheduleType: ScheduleType.ScheduleType;
  readonly status: EmailCycleStatus.EmailCycleStatus;
  readonly isScheduleEditable: boolean;


  constructor(
  id: LeoUUID, 
  scheduledAt: LeoTimestamp, 
  cycleName: BoundedName, 
  displayCycleName: BoundedName, 
  senderEmail: KeycloakEmail, 
  senderName: BoundedName, 
  emailContent: EmailContent, 
  scheduleType: ScheduleType.ScheduleType, 
  status: EmailCycleStatus.EmailCycleStatus, 
  isScheduleEditable: boolean
  ) {
    this.id = id;
    this.scheduledAt = scheduledAt;
    this.cycleName = cycleName;
    this.displayCycleName = displayCycleName;
    this.senderEmail = senderEmail;
    this.senderName = senderName;
    this.emailContent = emailContent;
    this.scheduleType = scheduleType;
    this.status = status;
    this.isScheduleEditable = isScheduleEditable;
  }

  static fromDTO(dto: EmailCycleDetailsDTO): EmailCycleDetails {
    const id = getLeoUuid(dto, "id", InvalidEmailCycleDetailsIdError);
    const scheduledAt = getLeoTimestamp(dto, "scheduledAt", InvalidEmailCycleDetailsScheduledAtError);
    const cycleName = BoundedName.fromDTO(getObject(dto, "cycleName", InvalidEmailCycleDetailsCycleNameError));
    const displayCycleName = BoundedName.fromDTO(getObject(dto, "displayCycleName", InvalidEmailCycleDetailsDisplayCycleNameError));
    const senderEmail = KeycloakEmail.fromDTO(getObject(dto, "senderEmail", InvalidEmailCycleDetailsSenderEmailError));
    const senderName = BoundedName.fromDTO(getObject(dto, "senderName", InvalidEmailCycleDetailsSenderNameError));
    const emailContent = EmailContent.fromDTO(getObject(dto, "emailContent", InvalidEmailCycleDetailsEmailContentError));
    const scheduleType = ScheduleType.fromDTO(getObject(dto, "scheduleType", InvalidEmailCycleDetailsScheduleTypeError));
    const status = EmailCycleStatus.fromDTO(getObject(dto, "status", InvalidEmailCycleDetailsStatusError));
    const isScheduleEditable = getBoolean(dto, "isScheduleEditable", InvalidEmailCycleDetailsIsScheduleEditableError);
    return new EmailCycleDetails(
      id,
      scheduledAt,
      cycleName,
      displayCycleName,
      senderEmail,
      senderName,
      emailContent,
      scheduleType,
      status,
      isScheduleEditable
    );
  }

  toDTO(): EmailCycleDetailsDTO {
    const dto: EmailCycleDetailsDTO = {
        id: this.id.toDTO(),
        scheduledAt: this.scheduledAt.toDTO(),
        cycleName: this.cycleName.toDTO(),
        displayCycleName: this.displayCycleName.toDTO(),
        senderEmail: this.senderEmail.toDTO(),
        senderName: this.senderName.toDTO(),
        emailContent: this.emailContent.toDTO(),
        scheduleType: this.scheduleType.toDTO(),
        status: EmailCycleStatus.toDTO(this.status),
        isScheduleEditable: this.isScheduleEditable
    };
    return dto;
  }
  }

EmailCycleDetails.prototype.toString = function toString(): string {
  return `EmailCycleDetails(id='${this.id}',scheduledAt='${this.scheduledAt}',cycleName='${this.cycleName}',displayCycleName='${this.displayCycleName}',senderEmail='${this.senderEmail}',senderName='${this.senderName}',emailContent='${this.emailContent}',scheduleType='${this.scheduleType}',status='${this.status}',isScheduleEditable='${this.isScheduleEditable}')`;
};

export class InvalidEmailCycleDetailsError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleDetailsError";
  }
}
export class InvalidEmailCycleDetailsIdError extends InvalidEmailCycleDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleDetailsIdError";
  }
}

export class InvalidEmailCycleDetailsScheduledAtError extends InvalidEmailCycleDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleDetailsScheduledAtError";
  }
}

export class InvalidEmailCycleDetailsCycleNameError extends InvalidEmailCycleDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleDetailsCycleNameError";
  }
}

export class InvalidEmailCycleDetailsDisplayCycleNameError extends InvalidEmailCycleDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleDetailsDisplayCycleNameError";
  }
}

export class InvalidEmailCycleDetailsSenderEmailError extends InvalidEmailCycleDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleDetailsSenderEmailError";
  }
}

export class InvalidEmailCycleDetailsSenderNameError extends InvalidEmailCycleDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleDetailsSenderNameError";
  }
}

export class InvalidEmailCycleDetailsEmailContentError extends InvalidEmailCycleDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleDetailsEmailContentError";
  }
}

export class InvalidEmailCycleDetailsScheduleTypeError extends InvalidEmailCycleDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleDetailsScheduleTypeError";
  }
}

export class InvalidEmailCycleDetailsStatusError extends InvalidEmailCycleDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleDetailsStatusError";
  }
}

export class InvalidEmailCycleDetailsIsScheduleEditableError extends InvalidEmailCycleDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleDetailsIsScheduleEditableError";
  }
}
